import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { ReadingPicker } from './components/ReadingPicker.jsx';

const INK = '#F5EEDC';

export const ComparisonPage = ({ id }) => {
  const { route } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: comparison, loading, refetch } = useApi(`/ink-density/comparisons/${id}`);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [readingIds, setReadingIds] = useState([]);
  // Readings picked but not yet in `comparison.readings` (that only refreshes
  // on save) — kept so the list/common-tags render them immediately instead
  // of silently dropping them until the next fetch.
  const [pendingReadings, setPendingReadings] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (comparison) {
      setName(comparison.name || '');
      setNotes(comparison.notes || '');
      setReadingIds((comparison.readings || []).map((r) => r.id));
      setPendingReadings([]);
    }
  }, [comparison]);

  // Readings currently in the comparison, in readingIds order — merges in
  // pendingReadings so a just-picked reading renders immediately.
  const orderedReadings = useMemo(() => {
    const pool = new Map([...(comparison?.readings || []), ...pendingReadings].map((r) => [r.id, r]));
    return readingIds.map((rid) => pool.get(rid)).filter(Boolean);
  }, [comparison, pendingReadings, readingIds]);

  const commonTags = useMemo(() => {
    const readings = orderedReadings;
    if (!readings.length) return [];
    const customers = new Set(readings.map((r) => r.customerName));
    const out = [];
    if (customers.size === 1 && readings[0].customerName) out.push(readings[0].customerName);
    let intersection = new Set(readings[0].tags || []);
    for (let i = 1; i < readings.length; i += 1) {
      const next = new Set(readings[i].tags || []);
      intersection = new Set([...intersection].filter((t) => next.has(t)));
    }
    const printTypes = new Set(readings.map((r) => r.printType || ''));
    if (printTypes.size === 1 && readings[0].printType) intersection.add(readings[0].printType);
    return [...out, ...intersection];
  }, [orderedReadings]);

  const uniqueFor = (r) => {
    const all = [...(r.tags || []), r.printType].filter(Boolean);
    return all.filter((t) => !commonTags.includes(t));
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.patch(`/ink-density/comparisons/${id}`, { name: name.trim(), notes: notes.trim() || null, readingIds });
      await refetch();
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  const renderReport = async () => {
    await save();
    const win = window.open('', '_blank');
    if (!win) { setError(new Error('Popup blocked — allow popups for this site to view the report.')); return; }
    try {
      const res = await fetch(`/api/v1/ink-density/comparisons/${id}/report`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { win.close(); setError(new Error(`Failed to generate report (${res.status})`)); return; }
      const html = await res.text();
      win.document.open(); win.document.write(html); win.document.close();
    } catch (e) {
      win.close();
      setError(e);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this comparison? Underlying readings remain.')) return;
    try {
      await api.delete(`/ink-density/comparisons/${id}`);
      route('/ink-density/comparisons');
    } catch (e) {
      setError(e);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= readingIds.length) return;
    const next = [...readingIds];
    [next[from], next[to]] = [next[to], next[from]];
    setReadingIds(next);
  };

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (!comparison) return <p class="text-sm text-text-tertiary">Not found.</p>;

  return (
    <div>
      <nav class="mb-2 text-[11px] text-text-tertiary">
        <a href="/ink-density" class="text-text-tertiary hover:text-text-primary">Ink density</a> ·
        <a href="/ink-density/comparisons" class="ml-1 text-text-tertiary hover:text-text-primary">Comparisons</a> ·
        <span class="ml-1">{name || 'Untitled'}</span>
      </nav>

      <section class="mb-4 flex items-start justify-between gap-4">
        <div class="flex-1">
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: INK, border: '1px solid rgba(245,238,220,0.4)' }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: INK }}>Comparison</span>
          </div>
          <input
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
            placeholder="Comparison name"
            class="block w-full border-b border-dashed border-border-tertiary bg-transparent py-1 font-serif text-[22px] font-medium text-text-primary focus:border-brand focus:outline-none"
          />
          <input
            value={notes}
            onInput={(e) => setNotes(e.currentTarget.value)}
            placeholder="Notes (optional)…"
            class="mt-1 block w-full bg-transparent py-1 text-[12px] text-text-secondary focus:outline-none"
          />
        </div>
        <div class="flex shrink-0 gap-2">
          {isAdmin && <Button variant="ghost" onClick={remove} class="text-ink-magenta">Delete</Button>}
          <Button variant="ghost" onClick={renderReport}>Render report</Button>
          <Button variant="primary" onClick={save} disabled={busy || !name.trim()}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </section>

      <div class="mb-4 rounded-md bg-bg-card p-3">
        <p class="mb-2 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Common across all readings (auto)</p>
        {commonTags.length === 0 ? (
          <p class="text-[12px] text-text-muted">{orderedReadings.length < 2 ? 'Add 2+ readings to compute shared tags.' : 'No tags shared across all readings.'}</p>
        ) : (
          <div class="flex flex-wrap gap-1.5">
            {commonTags.map((t) => (
              <span key={t} class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[11px]"
                style={{ background: 'rgba(95,170,255,0.10)', borderColor: 'rgba(95,170,255,0.35)', color: 'var(--color-text-info)' }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      <p class="mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Included readings · {orderedReadings.length}</p>

      <div class="space-y-1.5">
        {orderedReadings.map((r, idx) => {
          const u = uniqueFor(r);
          return (
            <div key={r.id} class="flex items-center gap-3 rounded-md border border-border-subtle bg-bg-card px-3 py-2">
              <div class="flex flex-col">
                <button onClick={() => move(idx, idx - 1)} disabled={idx === 0} class="text-[10px] text-text-tertiary disabled:opacity-30">▲</button>
                <button onClick={() => move(idx, idx + 1)} disabled={idx === orderedReadings.length - 1} class="text-[10px] text-text-tertiary disabled:opacity-30">▼</button>
              </div>
              <span class="font-mono text-[11px] text-text-tertiary">{idx + 1}</span>
              <div class="flex-1">
                <div class="flex items-baseline gap-2">
                  <span class="font-mono text-[13px] font-semibold text-text-primary">{r.jobNumber || `#${r.id}`}</span>
                  {r.jobName && <span class="text-[11px] text-text-secondary">— {r.jobName}</span>}
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-text-tertiary">
                  <span>{r.customerName}</span>
                  {r.profileLabel && <span>· {r.profileLabel}</span>}
                  {u.length > 0 && (
                    <>
                      <span class="ml-1 text-text-muted">Unique:</span>
                      {u.map((t) => (
                        <span key={t} class="rounded-full bg-bg-base px-1.5 py-0 font-mono">{t}</span>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <span class="text-[10px] text-text-muted">{r.readingDate || ''}</span>
              <button onClick={() => setReadingIds(readingIds.filter((x) => x !== r.id))} class="text-[14px] text-text-tertiary hover:text-ink-magenta" aria-label="Remove">×</button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        class="mt-3 w-full rounded-md border border-dashed border-border-tertiary py-2 text-center text-[12px] text-text-tertiary hover:text-text-primary"
      >+ Add reading</button>

      {error && (
        <p class="mt-3 text-[13px] font-semibold text-ink-magenta">
          {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
        </p>
      )}

      <ReadingPicker
        open={pickerOpen}
        excludeIds={readingIds}
        onClose={() => setPickerOpen(false)}
        onPick={(r) => { setReadingIds([...readingIds, r.id]); setPendingReadings((p) => [...p, r]); setPickerOpen(false); }}
      />
    </div>
  );
};
